import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { Box, MediaObject } from '@zillow/constellation-10';
import {
  ChatAvatar,
  ChatBubbleWithLinkFeatureToggle,
  isMessageStatusVariant,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import {
  MessageList_GetConversationDocument,
  MessageList_GetConversationQuery,
  MessageList_GetConversationQueryVariables,
  useAddMessage_AddMessageToConversationV2Mutation,
} from '../../../../types/GraphqlTypes';
import useFeatureFlagsHelper from '../../../../hooks/useFeatureFlagsHelper';
import { useUserWarnings } from '../../../../hooks/useUserWarnings';
import stringUtils from '../../../../utils/stringUtils';
import MessageAttachments, { AttachmentsForMessage } from '../../../MessageAttachments';
import type {
  ConversationIdentity,
  HandleOpenDeleteAttachmentsModal,
  HandleOpenImagesViewer,
  OnResendMessageComplete,
} from '../../../MessageListItems';
import { logApolloError } from '../../../../utils/errorUtils';
import type { TriggerLocation } from '../../../../constants/clickstreamEvents/globals';
import ChatBubbleWithActions from '../../../ChatBubbleWithActions';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../../../constants/clickstreamEvents/externalLinkWarningModal';

type Props = {
  attachments: AttachmentsForMessage;
  conversation: ConversationIdentity;
  onResendMessageComplete?: OnResendMessageComplete;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  senderName: string;
  shouldDisplaySentStatus: boolean;
  uiTreatmentType: string;
  messageId: string;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
  triggerLocation: TriggerLocation;
};

const getAttachmentZDocIdsForAddMessageV2Mutation = (attachments: AttachmentsForMessage): Array<string> => {
  return attachments.map(({ zDocId }) => zDocId || '');
};

const ListingMessage = ({
  attachments,
  conversation,
  onResendMessageComplete,
  isMessageOwner,
  messageContent,
  messageDateMs,
  senderName,
  shouldDisplaySentStatus,
  messageId,
  uiTreatmentType,
  openDeleteAttachmentsModal,
  openImagesViewer,
  triggerLocation,
}: Props) => {
  const apolloClient = useApolloClient();
  const { enableMessageToolbar, enableMessageLinkInSLW } = useFeatureFlagsHelper();
  const { conversationId, listingAlias = '', clientTimeZone } = conversation;

  const { externalLinkWarningData } = useUserWarnings();

  const [addMessageMutation] = useAddMessage_AddMessageToConversationV2Mutation();
  const statusFromUiTreatment = uiTreatmentType.split('-').length > 1 ? uiTreatmentType.split('-')[1] : undefined;
  const validStatus = isMessageStatusVariant(statusFromUiTreatment) ? statusFromUiTreatment : MESSAGE_STATUS.SENT;
  const [sendStatus, setSendStatus] = useState<MessageStatusVariant | undefined>(validStatus);

  // Sync from props when cache updates (e.g. send fails and AddMessage writes 'lowPriority-failed').
  useEffect(() => {
    setSendStatus(validStatus);
  }, [validStatus]);

  const hasAttachments = attachments && !!attachments.length && Object.keys(attachments[0]).length !== 0;
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const chatBubbleVariant = isMessageOwner ? 'messageOwner' : 'default';
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const handleResendMessage = useCallback(() => {
    setSendStatus(MESSAGE_STATUS.SENDING);
    const zDocIds = hasAttachments ? getAttachmentZDocIdsForAddMessageV2Mutation(attachments) : [];
    addMessageMutation({
      variables: {
        conversationId,
        listingAlias,
        message: messageContent,
        zDocIds,
      },
      onError(error) {
        logApolloError(error, {
          details: 'ListingMessage - Error resending failed message item.',
          conversationId,
          listingAlias,
          messageContent,
        });
        setSendStatus(MESSAGE_STATUS.FAILED);
      },
      onCompleted(data) {
        const newMessage = data?.ZRM_addMessageToConversationV2;
        const messageListData = apolloClient.cache.readQuery<
          MessageList_GetConversationQuery,
          MessageList_GetConversationQueryVariables
        >({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId,
            listingAlias,
            clientTimeZone,
          },
        });

        const currentMessages = messageListData?.ZRM_getConversationById.conversation || [];
        const tempMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
        const sentMessages = currentMessages.filter((message) => {
          const isTemp = message.messageId?.includes('temp');

          if (isTemp) {
            tempMessages.push(message);
          }
          return !isTemp;
        });

        const newConversation = [...sentMessages, newMessage, ...tempMessages];
        apolloClient.cache.writeQuery({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId,
            listingAlias,
          },
          data: {
            ZRM_getConversationById: {
              ...messageListData?.ZRM_getConversationById,
              conversation: newConversation,
            },
          },
        });

        // Remove this failed message item from the cache
        const id = apolloClient.cache.identify({ __typename: 'ZRM_Message', conversationId, messageId });

        apolloClient.cache.evict({ id });
        apolloClient.cache.gc();

        onResendMessageComplete?.({ conversation });
      },
    });
  }, [
    conversationId,
    listingAlias,
    clientTimeZone,
    messageContent,
    onResendMessageComplete,
    apolloClient,
    addMessageMutation,
    attachments,
    hasAttachments,
    messageId,
  ]);

  return (
    <MediaObject
      media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
      css={{
        alignItems: 'end',
        display: 'flex',
        flexDirection: isMessageOwner ? 'row-reverse' : 'row',
        gap: 0,
        whiteSpace: 'pre-wrap',
        width: '100%',
      }}
    >
      <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
        {!isMessageContentEmpty && (
          <>
            {enableMessageToolbar ? (
              <ChatBubbleWithActions
                senderName={senderName}
                variant={chatBubbleVariant}
                messageContent={messageContent}
                isMessageOwner={isMessageOwner}
                triggerLocation={triggerLocation}
              />
            ) : (
              <ChatBubbleWithLinkFeatureToggle
                senderName={senderName}
                variant={chatBubbleVariant}
                enableClickableLinks={!!enableMessageLinkInSLW}
                externalLinkWarningData={externalLinkWarningData}
                onExternalLinkWarningModalOpen={() => emitOpenExternalLinkWarningModalEvent(triggerLocation)}
                onExternalLinkClickInModal={() =>
                  emitExternalLinkClickedInExternalLinkWarningModalEvent(triggerLocation)
                }
                enableHistoryIntegration={true}
              >
                {messageContent}
              </ChatBubbleWithLinkFeatureToggle>
            )}
          </>
        )}

        {hasAttachments && (
          <MessageAttachments
            attachments={attachments}
            conversationId={conversationId}
            isMessageOwner={isMessageOwner}
            isMessageSent={sendStatus === MESSAGE_STATUS.SENT}
            messageId={messageId}
            openDeleteAttachmentsModal={openDeleteAttachmentsModal}
            openImagesViewer={openImagesViewer}
            triggerLocation={triggerLocation}
          />
        )}
      </Box>

      <MessageFooter
        isMessageOwner={isMessageOwner}
        messageDateMs={messageDateMs}
        resendMessage={handleResendMessage}
        sendStatus={sendStatus}
        shouldDisplaySentStatus={shouldDisplaySentStatus}
      />
    </MediaObject>
  );
};

export default ListingMessage;
